/**
 * 首屏 = **能力自检场**。
 *
 * 这不是占位页，是刻意留在每个项目里的东西：推送、角标、桌面图标、锁屏通知、长按手势
 * 这些**本地和 CI 都测不了**，只能拿真机开一页点一遍。把自检页做进项目，
 * 意味着任何时候都能在真机上一眼看出"这台设备上到底哪些能力是活的"。
 *
 * 开始写业务时把 `<SelfTest />` 挪到一个隐藏路由（比如 /__selftest）留着，别删。
 *
 * ⚠️ 首屏刻意**不依赖任何后端**：源一断就是白屏，而白屏本身就是故障信号。
 * 真实教训：有个首屏依赖第三方 API，那个接口在测试环境一直 404，首屏空了几个月没人发现。
 */

import { useEffect, useState } from 'react'

import {
  useAppUpdate,
  useBackgrounded,
  useInstall,
  useNeutralPath,
  useNotificationPermission,
  useOnline,
  usePwa,
  useUnread,
  useWakeLock,
} from '@pwaship/react'
import {
  ActionMenu,
  AppShell,
  BackdropCover,
  Button,
  ConfirmDialog,
  EmptyState,
  IconAddToHome,
  IconBell,
  IconCheck,
  IconInfo,
  ImageViewer,
  InstallBanner,
  InstallGuide,
  OfflineBar,
  PermissionPrimer,
  PullToRefresh,
  Sheet,
  SkinPicker,
  Skeleton,
  UpdatePrompt,
  useLongPress,
  useToast,
} from '@pwaship/ui'

declare const __APP_VERSION__: string
declare const __APP_BUILD__: string

export interface SelfTestScreenProps {
  path: string
  navigate(path: string): void
}

export default function SelfTestScreen({ path: fallbackPath, navigate: fallbackNavigate }: SelfTestScreenProps) {
  const [tab, setTab] = useState('check')

  // 开关 `neutralHistory` 时业务代码不用改，就是靠这三行：
  // 没开 → 用外面那个最简路由；开了 → 用框架的中性化路由（地址栏恒为 '/'）。
  const neutral = useNeutralPath()
  const path = neutral.enabled ? neutral.path : fallbackPath
  const navigate = neutral.enabled ? neutral.navigate : fallbackNavigate

  /*
   * 导航配置只有一份：窄屏它是底部 TabBar，宽屏它是左侧 SideNav。
   * 加一个页面不需要在两个地方各改一遍。
   */
  const tabs = [
    { key: 'check', label: '自检', icon: <IconCheck size={22} /> },
    { key: 'ui', label: '组件', icon: <IconInfo size={22} /> },
  ]

  const header = (
    <div className="app-header">
      <div>
        <h1 className="app-title">Carson OS</h1>
        <p className="app-sub">
          v{__APP_VERSION__} · build {__APP_BUILD__}
          {path !== '/' && ` · ${path}`}
        </p>
      </div>
    </div>
  )

  return (
    <>
      <OfflineBar />
      {/*
        一个 <AppShell> 管两种形态：窄屏是 Screen + 底部 TabBar，宽屏是侧栏 + 内容栏。
        **里面放的是同一份页面组件** —— 桌面不重写页面，否则此后每个功能都要改两遍。
      */}
      <AppShell
        items={tabs}
        active={tab}
        onChange={setTab}
        header={header}
        brand={<span>Carson</span>}
      >
        <UpdatePrompt />
        <InstallBanner />
        {tab === 'check' ? <SelfTest /> : <UiLab navigate={navigate} />}
      </AppShell>
    </>
  )
}

/* ─────────────────────────── 能力自检 ─────────────────────────── */

function Row({ label, value, note }: { label: string; value: boolean | string; note?: string }) {
  return (
    <div className="check-row">
      <span className="check-label">{label}</span>
      <span className={`check-value ${typeof value === 'boolean' ? (value ? 'is-ok' : 'is-no') : ''}`}>
        {typeof value === 'boolean' ? (value ? '可用' : '不可用') : value}
      </span>
      {note && <span className="check-note">{note}</span>}
    </div>
  )
}

function SelfTest() {
  const { platform, capabilities } = usePwa()
  const install = useInstall()
  const permission = useNotificationPermission()
  const unread = useUnread()
  const wakeLock = useWakeLock()
  const update = useAppUpdate()
  const online = useOnline()
  const toast = useToast()
  const [primer, setPrimer] = useState(false)
  const [swVersion, setSwVersion] = useState<string | null>(null)

  useEffect(() => {
    void update.version().then(setSwVersion)
  }, [update])

  return (
    <div className="section-stack">
      <section className="card">
        <h2 className="card-title">平台</h2>
        <Row label="浏览器" value={platform.browser} />
        <Row label="系统" value={platform.os} />
        <Row label="iOS 版本" value={platform.iosVersion ? String(platform.iosVersion) : '—'} note="未知按新版本处理" />
        <Row label="独立运行（已安装）" value={capabilities.standalone} />
        <Row label="安全上下文 HTTPS" value={capabilities.secureContext} />
        <Row label="在线" value={online} note="onLine 只说明有网卡，不代表连得上你的服务器" />
      </section>

      <section className="card">
        <h2 className="card-title">能力</h2>
        <Row label="Service Worker" value={capabilities.serviceWorker} />
        <Row label="SW 版本" value={swVersion ?? '未接管'} note="改完 SW 要完全退出 PWA 再打开" />
        <Row label="通知" value={capabilities.notification} note={platform.isIOS ? 'iOS 需从主屏图标打开' : undefined} />
        <Row label="Web Push" value={capabilities.push} />
        <Row label="应用角标" value={capabilities.badge} note="iOS 上还受通知权限控制" />
        <Row label="屏幕常亮" value={capabilities.wakeLock} />
        <Row label="系统安装提示" value={capabilities.installPrompt} note="iOS 没有，要走手动引导" />
        <Row label="Background Sync" value={capabilities.backgroundSync} note="iOS 没有，别依赖它" />
        <Row label="View Transitions" value={capabilities.viewTransitions} />
        <Row label="分享" value={capabilities.share} />
      </section>

      <section className="card">
        <h2 className="card-title">安装</h2>
        {install.standalone ? (
          <p className="card-note">已经是独立模式运行。</p>
        ) : (
          <>
            <InstallGuide />
            <div className="row-actions">
              {install.canPromptNatively && (
                <Button variant="primary" icon={<IconAddToHome size={16} />} onClick={() => void install.prompt()}>
                  安装
                </Button>
              )}
              <Button variant="ghost" onClick={install.markDone}>
                已添加，不再提示
              </Button>
            </div>
          </>
        )}
      </section>

      <section className="card">
        <h2 className="card-title">通知与角标</h2>
        <Row label="权限状态" value={permission.state} />
        <Row label="角标可用" value={unread.support.ok} note={unread.support.ok ? undefined : unread.support.reason} />
        <div className="row-actions">
          <Button variant="primary" icon={<IconBell size={16} />} onClick={() => setPrimer(true)}>
            申请通知权限
          </Button>
        </div>
        <div className="row-actions">
          <Button onClick={() => void unread.set(unread.total + 1)}>角标 +1（当前 {unread.total}）</Button>
          <Button onClick={() => void unread.set(0)}>清零并关掉残留通知</Button>
        </div>
        <p className="card-note">
          判据：角标清零时，通知中心里那条横幅也必须消失。两者是互不相干的两套东西，只清一个是踩过的故障。
        </p>
        <PermissionPrimer open={primer} onClose={() => setPrimer(false)} onGranted={() => toast.success('已授权')} />
      </section>

      <section className="card">
        <h2 className="card-title">屏幕常亮</h2>
        <Row label="支持" value={wakeLock.supported} />
        <Row label="当前" value={wakeLock.active} />
        <div className="row-actions">
          <Button onClick={() => void wakeLock.acquire()}>开启</Button>
          <Button onClick={() => void wakeLock.release()}>关闭</Button>
        </div>
        <p className="card-note">判据：切到别的应用再切回来，常亮仍然有效（锁会被系统收走，必须自动重新申请）。</p>
      </section>

      <section className="card">
        <h2 className="card-title">离线</h2>
        <p className="card-note">
          判据：打开飞行模式，杀掉应用再从桌面图标打开，**必须仍然进得到这个页面**。
          进不来就说明预缓存或导航兜底没生效。
        </p>
        <div className="row-actions">
          <Button onClick={() => void update.check()}>检查更新</Button>
        </div>
      </section>
    </div>
  )
}

/* ─────────────────────────── 隐私与痕迹 ─────────────────────────── */

/**
 * 威胁模型：**拿到这台已解锁手机的人**。不打开应用能看出什么？
 * 前面那些卡片测的是"能不能用"，这一张测的是"看不看得见"。
 */
function PrivacyLab() {
  const neutral = useNeutralPath()
  const backgrounded = useBackgrounded()
  const [cover, setCover] = useState(false)

  return (
    <section className="card">
      <h2 className="card-title">隐私与痕迹</h2>
      <Row
        label="中性化路由"
        value={neutral.enabled}
        note={neutral.enabled ? '地址栏恒为 /' : '默认关。main.tsx 里 NEUTRAL_HISTORY 改 true'}
      />
      <Row label="内部路径" value={neutral.path} />
      <Row label="地址栏" value={typeof window === 'undefined' ? '—' : window.location.pathname} />
      <Row label="当前处于后台/失焦" value={backgrounded} note="visibilitychange / pagehide / blur 三个都听" />
      <div className="row-actions">
        <Button onClick={() => setCover((v) => !v)}>{cover ? '关掉快照遮盖' : '打开快照遮盖'}</Button>
      </div>
      <p className="card-note">
        打开之后切到别的应用，再进多任务界面看这张卡片——应该看不到内容。
        ⚠️ 它<strong>降低概率，不消除风险</strong>：iOS 上截图时机不由网页决定，
        系统完全可能在 visibilitychange 派发之前就截了。别在产品文案里写「防止截屏」。
      </p>
      {cover && (
        <BackdropCover>
          <strong style={{ fontSize: 20 }}>Carson OS</strong>
        </BackdropCover>
      )}
    </section>
  )
}

/* ─────────────────────────── 组件实验室 ─────────────────────────── */

function UiLab({ navigate }: { navigate(path: string): void }) {
  const toast = useToast()
  const [sheet, setSheet] = useState(false)
  const [confirm, setConfirm] = useState(false)
  const [nested, setNested] = useState(false)
  const [viewer, setViewer] = useState(false)
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null)
  const [loading, setLoading] = useState(false)

  const longPress = useLongPress({
    onLongPress: ({ x, y }) => setMenu({ x, y }),
    onClick: () => toast.info('这是普通点击'),
  })

  const rowPress = useLongPress({
    onLongPress: ({ x, y }) => setMenu({ x, y }),
    onClick: () => toast.info('整行被点了'),
  })

  return (
    <PullToRefresh
      onRefresh={async () => {
        await new Promise((r) => setTimeout(r, 900))
        toast.success('刷新完成')
      }}
    >
      <div className="section-stack">
        <section className="card">
          <h2 className="card-title">皮肤</h2>
          <SkinPicker />
          <p className="card-note">
            皮肤 = 一整套设计 token（配色 / 字阶 / 间距 / 圆角 / 阴影 / 动效 / 层级）。
            所有组件只读 token，所以换皮肤不用动任何组件。改 <code>src/styles/skin.ts</code>
            就是改这个应用的皮肤。「跟随系统」是三态里的一个独立状态，不是开关——
            做成开关等于强迫用户挑一个具体值，然后他的手机入夜时应用不跟着变。
          </p>
        </section>

        <section className="card">
          <h2 className="card-title">反馈</h2>
          <div className="row-actions">
            <Button onClick={() => toast.info('普通提示')}>info</Button>
            <Button onClick={() => toast.success('成功了')}>success</Button>
            <Button onClick={() => toast.warn('需要注意')}>warn</Button>
            <Button
              variant="danger"
              onClick={() => toast.error('上传失败：网络不可用', { action: { label: '重试', onClick: () => toast.success('重试成功') } })}
            >
              error（不自动消失）
            </Button>
          </div>
          <p className="card-note">错误默认常驻并带一个重试入口——失败必须有可见的落点，不能凭空消失。</p>
        </section>

        <PrivacyLab />

        <section className="card">
          <h2 className="card-title">弹层</h2>
          <div className="row-actions">
            <Button onClick={() => setSheet(true)}>底部抽屉（可下拉关闭）</Button>
            <Button variant="danger" onClick={() => setConfirm(true)}>
              破坏性确认
            </Button>
          </div>
          <p className="card-note">判据：安卓上按返回键应该关掉弹层，而不是退出整个应用。</p>
        </section>

        <section className="card">
          <h2 className="card-title">手势</h2>
          <div className="press-target" {...longPress}>
            长按我（500ms）
          </div>

          {/*
            长按手势装在**容器**上、而容器里还有个按钮 —— 这是最容易翻车的组合：
            手势要 preventDefault() + setPointerCapture()，两者都会让里面的按钮点不动，
            而视觉上完全正常。框架的判据是"落点是不是宿主里的另一个可交互元素"。
          */}
          <div className="press-row" {...rowPress}>
            <span>整行可长按，行里的按钮要照常能点</span>
            <Button size="sm" onClick={() => toast.success('按钮点到了')}>
              更多
            </Button>
          </div>

          <div className="row-actions">
            <Button onClick={() => setViewer(true)}>看图器（双指缩放 / 下滑关闭 / 左右翻页）</Button>
          </div>
          <p className="card-note">判据：iOS 上长按不能选中文字、不能弹放大镜；放大后横拖是平移，不能翻页。</p>
        </section>

        <section className="card">
          <h2 className="card-title">状态</h2>
          <div className="row-actions">
            <Button loading={loading} onClick={() => {
              setLoading(true)
              setTimeout(() => setLoading(false), 1200)
            }}>
              加载中不跳宽度
            </Button>
            <Button onClick={() => navigate('/deep/link/demo')}>模拟深链跳转</Button>
          </div>
          <Skeleton lines={3} />
          <EmptyState
            icon={<IconInfo size={30} />}
            title="这里还没有内容"
            description="空状态必须说清接下来能做什么，只写「暂无数据」等于什么都没说。"
            action={<Button variant="primary">新建一个</Button>}
          />
        </section>
      </div>

      <Sheet
        open={sheet}
        onClose={() => setSheet(false)}
        title="底部抽屉"
        footer={
          <div className="row-actions">
            {/* 抽屉里再开一个确认框：一次 Esc 只该关掉最上面那一层。
                Esc 监听都挂在 document 上，stopPropagation 拦不住同层的其他监听，
                所以框架内部是靠"弹层栈，栈顶胜出"做的。 */}
            <Button onClick={() => setNested(true)}>抽屉内确认</Button>
            <Button variant="primary" onClick={() => setSheet(false)}>
              知道了
            </Button>
          </div>
        }
      >
        <p className="card-note">
          往下拖可以关闭，拖得越多越沉；快速下甩也能关。内容滚到顶之后再往下拖才会触发关闭，
          否则长内容会变成"一滑就关，根本看不了"。
        </p>
        {Array.from({ length: 12 }, (_, i) => (
          <p key={i} className="card-note">
            第 {i + 1} 行，用来测试内部滚动与关闭手势不打架。
          </p>
        ))}
        <ConfirmDialog
          open={nested}
          title="抽屉里的确认框"
          description="按一次 Esc 应该只关掉这个框，抽屉要留着。"
          confirmText="好"
          cancelText="取消"
          onCancel={() => setNested(false)}
          onConfirm={() => setNested(false)}
        />
      </Sheet>

      <ConfirmDialog
        open={confirm}
        title="删除这条记录？"
        description="删除后无法恢复。说清楚会发生什么、能不能撤销——「确定吗？」是没有信息量的。"
        destructive
        confirmText="删除"
        onCancel={() => setConfirm(false)}
        onConfirm={() => {
          setConfirm(false)
          toast.success('已删除')
        }}
      />

      <ActionMenu
        open={menu !== null}
        anchor={menu}
        onClose={() => setMenu(null)}
        title="操作"
        items={[
          { key: 'copy', label: '复制', onSelect: () => toast.info('已复制') },
          { key: 'share', label: '分享', onSelect: () => toast.info('分享') },
          { key: 'delete', label: '删除', destructive: true, onSelect: () => toast.warn('删除') },
        ]}
      />

      <ImageViewer
        open={viewer}
        onClose={() => setViewer(false)}
        images={[
          { src: '/icon-512.png', alt: '图标 1' },
          { src: '/icon-maskable-512.png', alt: '图标 2' },
        ]}
      />
    </PullToRefresh>
  )
}
